/**
 * Writes the finished report files into a folder the operator chooses, using the
 * File System Access API, so the PDF, spreadsheet and raw evidence all land in
 * the same place as the source file rather than scattering into Downloads.
 *
 * Chrome and Edge support this. Anywhere it is missing we fall back to ordinary
 * downloads, so the tool still works, just with less control over where things go.
 */

export const isDirectoryPickerSupported = () => typeof window !== "undefined" && typeof window.showDirectoryPicker === "function"

/**
 * Ask for a folder. Returns null if the operator dismisses the picker, which is
 * a normal outcome and not an error.
 */
export const pickOutputDirectory = async () => {
	if (!isDirectoryPickerSupported()) return null

	try {
		return await window.showDirectoryPicker({ id: "ghd-case-lookup", mode: "readwrite", startIn: "downloads" })
	} catch (error) {
		// AbortError means the picker was dismissed
		if (error?.name === "AbortError") return null

		throw error
	}
}

/**
 * Confirm we still hold write permission on a folder chosen earlier in the
 * session, re-prompting if the browser has dropped it.
 */
export const ensureWritePermission = async (directoryHandle) => {
	if (!directoryHandle?.queryPermission) return true

	const options = { mode: "readwrite" }

	if ((await directoryHandle.queryPermission(options)) === "granted") return true

	return (await directoryHandle.requestPermission(options)) === "granted"
}

/**
 * Last-resort download, used when there is no directory handle to write into.
 */
const downloadBlob = (fileName, blob) => {
	const url = URL.createObjectURL(blob)
	const link = document.createElement("a")

	link.href = url
	link.download = fileName
	document.body.appendChild(link)
	link.click()
	document.body.removeChild(link)

	// Give the browser a moment to start the download before revoking
	setTimeout(() => URL.revokeObjectURL(url), 10000)
}

/**
 * Write one file, into the chosen folder where possible and to Downloads
 * otherwise. Reports which route it took so the page can tell the operator where
 * the files actually are.
 */
export const writeOutputFile = async (directoryHandle, fileName, blob) => {
	if (directoryHandle) {
		const handle = await directoryHandle.getFileHandle(fileName, { create: true })
		const writable = await handle.createWritable()

		await writable.write(blob)
		await writable.close()

		return { fileName, destination: "folder" }
	}

	downloadBlob(fileName, blob)

	return { fileName, destination: "download" }
}
